/**
 * Util function that checks flight items, flight views, approval request and approval policy
 * to determine if the approval request restore in the checkout is valid
 */
import { FlightSearchBudgets } from 'lib/flights/flightUtils'
import { getMaxAllowedBudget, isAllDatesMatchApprovalRequest, isRouteMatchApprovalRequest } from './approvalRequestUtils'
import { calculateFlightBudget, FlightBudgetInfo } from './calculateTripRequestBudget'
import { isOverBudget } from './budgetComparison'

export enum FlightApprovalInvalidReason {
  ROUTE = 'route',
  DATE = 'date',
  BUDGET = 'budget',
}

const CART_EVALUATION_REASON_TO_INVALID_REASON: Record<string, FlightApprovalInvalidReason> = {
  ROUTE: FlightApprovalInvalidReason.ROUTE,
  DATE: FlightApprovalInvalidReason.DATE,
  BUDGET: FlightApprovalInvalidReason.BUDGET,
}

type GetFlightApprovalInvalidReasonParams = {
  approvalRequest: App.BusinessTraveller.ApprovalRequest | undefined
  approvalPolicy: App.BusinessTraveller.ApprovalPolicy | null
  flightItems: Array<App.Checkout.FlightItem>
  flightViews: Array<App.Checkout.FlightItemView>
  flightSearchBudgets: Array<FlightSearchBudgets>
  cartEvaluationTripRequest?: App.BusinessTraveller.CartEvaluationTripRequestValidation | null
  cartEvaluationApprovalPolicy?: App.BusinessTraveller.CartEvaluationApprovalPolicy
}

export type FlightApprovalValidationResult =
  | { invalidReason: FlightApprovalInvalidReason.ROUTE | FlightApprovalInvalidReason.DATE | undefined }
  | { invalidReason: FlightApprovalInvalidReason.BUDGET; flightBudgetInfo: FlightBudgetInfo }

export function getFlightApprovalInvalidReason(params: GetFlightApprovalInvalidReasonParams): FlightApprovalValidationResult {
  // Item policies on: the backend cart evaluation owns the verdict
  if (params.cartEvaluationTripRequest !== undefined) {
    return getCartEvaluationInvalidReason(params, params.cartEvaluationTripRequest)
  }

  return getLegacyInvalidReason(params)
}

// Maps the backend verdict onto the same invalid reasons the legacy validation produces
function getCartEvaluationInvalidReason(
  params: GetFlightApprovalInvalidReasonParams,
  tripRequest: App.BusinessTraveller.CartEvaluationTripRequestValidation | null,
): FlightApprovalValidationResult {
  const { approvalRequest, cartEvaluationApprovalPolicy, flightViews, flightSearchBudgets } = params

  const invalidReason = tripRequest && !tripRequest.isValid
    ? tripRequest.invalidReasons.map((entry) => CART_EVALUATION_REASON_TO_INVALID_REASON[entry.reason]).find(Boolean)
    : undefined

  if (invalidReason !== FlightApprovalInvalidReason.BUDGET) {
    return { invalidReason }
  }

  // The budget display uses the audit numbers returned by cart evaluate.
  if (!approvalRequest || !cartEvaluationApprovalPolicy || !tripRequest?.audit) {
    return { invalidReason: undefined }
  }

  const flightBudgetInfo = calculateFlightBudget({
    flightViews,
    flightSearchBudgets,
    flightBudget: tripRequest.audit.bufferedApprovedTotal,
  })

  return { invalidReason: FlightApprovalInvalidReason.BUDGET, flightBudgetInfo }
}

/** @deprecated remove when the lebt_item_policies_enabled flag is on in production */
function getLegacyInvalidReason(params: GetFlightApprovalInvalidReasonParams): FlightApprovalValidationResult {
  const { approvalRequest, approvalPolicy, flightItems, flightViews, flightSearchBudgets } = params

  if (!approvalRequest || flightItems.length === 0 || flightViews.length === 0) {
    return { invalidReason: undefined }
  }

  if (!isRouteMatchApprovalRequest(approvalRequest, flightItems, flightViews)) {
    return { invalidReason: FlightApprovalInvalidReason.ROUTE }
  }

  if (!isAllDatesMatchApprovalRequest(approvalRequest, flightItems, flightViews)) {
    return { invalidReason: FlightApprovalInvalidReason.DATE }
  }

  // Check if the restored flight item total cost exceeds the (approved cost + price buffer)
  if (approvalPolicy) {
    const maxAllowedBudget = getMaxAllowedBudget(approvalRequest.total, approvalPolicy.priceBuffer)
    if (maxAllowedBudget > 0) {
      const flightBudgetInfo = calculateFlightBudget({
        flightViews,
        flightSearchBudgets,
        flightBudget: maxAllowedBudget,
      })
      const isOverMaxAllowedBudget = isOverBudget(flightBudgetInfo.budgetedFlightTotalWithOtherFees, maxAllowedBudget)
      const isApprovalRequired = approvalPolicy.scope === 'ALWAYS' || approvalPolicy.scope === 'OUTOFPOLICYONLY'
      // If the flight item total cost exceeds the (approved cost + price buffer) and approval is required, return the invalid reason
      if (isOverMaxAllowedBudget && isApprovalRequired) {
        return { invalidReason: FlightApprovalInvalidReason.BUDGET, flightBudgetInfo }
      }
    }
  }

  return { invalidReason: undefined }
}
